import { useState, useEffect, useCallback, useRef } from "react";
import { useTableStore } from "state/TableStore";
import { useConfigService } from "./useConfigService";

export type SortOrder = 'NEWEST' | 'OLDEST';

export interface DashboardPreferences {
    isDefaultLandingPage: boolean;
    thresholdLow: number;
    thresholdHigh: number;
    sortOrders: Record<string, SortOrder>;
}

const LOCAL_STORAGE_KEY = "dashboardPreferences";

const DEFAULT_PREFERENCES: DashboardPreferences = {
    isDefaultLandingPage: false,
    thresholdLow: 20,
    thresholdHigh: 30,
    sortOrders: {},
};

const readFromLocalStorage = (): DashboardPreferences => {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!raw) return DEFAULT_PREFERENCES;
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(raw) };
    } catch {
        return DEFAULT_PREFERENCES;
    }
};

const writeToLocalStorage = (prefs: DashboardPreferences) => {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prefs));
    } catch (err) {
        console.log('Error writing dashboard preferences to localStorage: ', err);
    }
};

export const useDashboardPreferences = () => {
    const [preferences, setPreferences] = useState<DashboardPreferences>(readFromLocalStorage);
    const [isPost, setIsPost] = useState(false);
    const [configBody, setConfigBody] = useState<any>(null);
    const hasSyncedFromS3 = useRef(false);

    const configService = useTableStore(state => state.configService);
    const { updatingConfig } = useConfigService(
        'sticky-settings.json',
        configBody,
        false,
        isPost,
        false
    );

    useEffect(() => {
        if (!hasSyncedFromS3.current && configService.dashboardPreferences) {
            hasSyncedFromS3.current = true;
            const merged = { ...DEFAULT_PREFERENCES, ...configService.dashboardPreferences };
            setPreferences(merged);
            writeToLocalStorage(merged);
        }
    }, [configService.dashboardPreferences]);

    const persist = useCallback((updated: DashboardPreferences) => {
        setPreferences(updated);
        writeToLocalStorage(updated);

        const currentSavedSearches = (() => {
            const internalArray = useTableStore.getState().internalSearchArray;
            if (Array.isArray(internalArray)) return internalArray;
            return Object.values(internalArray ?? {});
        })();

        setConfigBody({
            savedSearches: currentSavedSearches,
            dashboardPreferences: updated,
        });
        setIsPost(true);
    }, [preferences]);

    const setIsDefaultLandingPage = useCallback((value: boolean) => {
        persist({ ...preferences, isDefaultLandingPage: value });
    }, [preferences, persist]);

    const setThresholds = useCallback((low: number, high: number) => {
        persist({ ...preferences, thresholdLow: low, thresholdHigh: high });
    }, [preferences, persist]);

    const setSortOrder = useCallback((statusValue: string, order: SortOrder) => {
        persist({
            ...preferences,
            sortOrders: { ...preferences.sortOrders, [statusValue]: order },
        });
    }, [preferences, persist]);

    useEffect(() => {
        if (isPost) setIsPost(false);
    }, [isPost]);

    return {
        preferences,
        setIsDefaultLandingPage,
        setThresholds,
        setSortOrder,
        saving: updatingConfig,
    };
};